import React, { useEffect, useState, useCallback } from 'react';
import { Col, Row, Form } from 'react-bootstrap';
import {
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

import CHARTCOLORS from '../../helpers/chartColors';
import MONTHNAMES from '../../helpers/monthsAbbreviated';
import DownloadChartData from '../DownloadChartData';
import { UTILITY_TYPE_UNITS } from './regressors';
import EnergyTracerTable from '../tables';

const styles = { unitHeader: { marginBottom: '0.5em', width: '100px' } };

export default function PerformanceCalenderAnalysis(props) {
  const {
    organization_id,
    utilityType,
    regressionType,
    baselineData,
    performanceData,
    simpleBaseline,
    performanceModel,
  } = props;

  const [showTotal, setShowTotal] = useState(false);
  const [calenderData, setCalenderData] = useState([]);

  const [simpleTotal, setSimpleTotal] = useState(0);
  const [adjustedTotal, setAdjustedTotal] = useState(0);
  const [performanceTotal, setPerformanceTotal] = useState(0);
  const [performanceEvalTotal, setPerformanceEvalTotal] = useState(0);

  const [simpleSavings, setSimpleSavings] = useState(0);
  const [performanceSavings, setPerformanceSavings] = useState(0);

  const [chartDownloadHeaders, setChartDownloadHeaders] = useState([]);
  const [chartDownloadKeys, setChartDownloadKeys] = useState([]);

  const [totalKeys, setTotalKeys] = useState([]);

  const formatBaselineData = useCallback(() => {
    if (baselineData.length > 0 && simpleBaseline.length > 0) {
      let temp_data = [];

      let simple_total = 0;
      let adjusted_total = 0;
      let performance_total = 0;
      let performance_eval_total = 0;

      let simple_savings = 0;
      let performance_savings = 0;


      for (let i = 0; i < baselineData.length; i++) {
        let temp_obj = {};
        let temp_simple = 0;
        let temp_performance = 0;
        temp_obj['baseline'] = parseFloat(baselineData[i].toFixed(2));
        temp_obj['performance'] = parseFloat(
          performanceData['energy'][i].toFixed(2)
        );
        temp_obj['simple_baseline'] = parseFloat(simpleBaseline[i].toFixed(2));
        temp_simple = temp_obj['simple_baseline'] - temp_obj['performance'];
        temp_performance = temp_obj['baseline'] - temp_obj['performance'];
        if (performanceModel.length > 0) {
          temp_obj['performance_eval'] = parseFloat(
            performanceModel[i].toFixed(2)
          );
        } else {
          temp_obj['performance_eval'] = 0;
        }

        adjusted_total += temp_obj['baseline'] * performanceData['days'][i];
        performance_total += temp_obj['performance'] * performanceData['days'][i];
        simple_total += temp_obj['simple_baseline'] * performanceData['days'][i];
        performance_eval_total += temp_obj['performance_eval'] * performanceData['days'][i];

        if (showTotal) {
          temp_obj['baseline'] *= performanceData['days'][i];
          temp_obj['performance'] *= performanceData['days'][i];
          temp_obj['simple_baseline'] *= performanceData['days'][i];
          temp_obj['performance_eval'] *= performanceData['days'][i];
        }



        temp_simple *= performanceData['days'][i];
        temp_performance *= performanceData['days'][i];

        simple_savings += temp_simple;
        performance_savings += temp_performance;

        temp_obj['name'] =
          MONTHNAMES[performanceData['months'][i]] +
          ' ' +
          performanceData['years'][i];
        temp_data[i] = temp_obj;
      }
      setCalenderData(temp_data);
      setSimpleSavings(simple_savings.toLocaleString('en-US'));
      setPerformanceSavings(performance_savings.toLocaleString('en-US'));

      setAdjustedTotal(adjusted_total.toLocaleString('en-US'));
      setPerformanceTotal(performance_total.toLocaleString('en-US'));
      setSimpleTotal(simple_total.toLocaleString('en-US'));
      setPerformanceEvalTotal(performance_eval_total.toLocaleString('en-US'));

    } else {
      setCalenderData([]);
      setSimpleSavings(0);
      setPerformanceSavings(0);
    }
  }, [
    baselineData,
    performanceData,
    simpleBaseline,
    performanceModel,
    showTotal,
  ]);

  useEffect(() => {
    let headers = [
      'Date',
      'Simple Baseline',
      'Adjusted Baseline',
      'Performance',
    ];
    let keys = ['name', 'simple_baseline', 'baseline', 'performance'];
    if (performanceModel.length > 0) {
      headers.push('Performance Model');
      keys.push('performance_eval');
    }
    setChartDownloadHeaders(headers);
    setChartDownloadKeys(keys);
  }, [calenderData, performanceModel]);

  useEffect(() => {
    formatBaselineData();
  }, [formatBaselineData]);

  useEffect(() => {
    if (performanceModel.length > 0) {
      setTotalKeys([
        { name: 'Simple Baseline', value: simpleTotal },
        { name: 'Adjusted Baseline', value: adjustedTotal },
        { name: 'Performance', value: performanceTotal },
        { name: 'Performance Eval', value: performanceEvalTotal },])
    }
    else {
      setTotalKeys([
        { name: 'Simple Baseline', value: simpleTotal },
        { name: 'Adjusted Baseline', value: adjustedTotal },
        { name: 'Performance', value: performanceTotal },
      ])
    }
  }, [performanceModel, simpleTotal, adjustedTotal, performanceTotal, performanceEvalTotal])


  return (
    <>
      {baselineData.length === 0 ? (
        <div>Select Performance Period</div>
      ) : (
        <Row>
          <Row
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Row style={{ width: '800px' }}>
              <Form.Control
                style={styles.unitHeader}
                as='select'
                size='sm'
                onChange={(e) =>
                  setShowTotal(e.target.value === 'true' ? true : false)
                }
              >
                <option key={'usage-per-day'} value={false}>
                  {UTILITY_TYPE_UNITS[regressionType][utilityType] + '/day'}
                </option>
                <option key={'usage-total'} value={true}>
                  {UTILITY_TYPE_UNITS[regressionType][utilityType]}
                </option>
              </Form.Control>
              <DownloadChartData
                organization_id={organization_id}
                data={calenderData}
                headers={chartDownloadHeaders}
                keys={chartDownloadKeys}
                chart_type={'line'}
              />
              <br></br>
              <br></br>
              <ResponsiveContainer height={350} debounce={calenderData.length}>
                <LineChart
                  data={calenderData}
                  height={350}
                  width={800}
                  margin={{ right: 25 }}
                >
                  <CartesianGrid />
                  <XAxis dataKey='name' type='category' interval={1} />
                  <YAxis
                    tickFormatter={(tick) => {
                      return tick.toLocaleString();
                    }}
                  />
                  <Tooltip
                    formatter={(value) =>
                      !isNaN(Number(value))
                        ? Number(value).toLocaleString()
                        : value
                    }
                  />
                  <Legend />
                  <Line
                    name='Simple Baseline'
                    dataKey={'simple_baseline'}
                    stroke={CHARTCOLORS[2]}
                  />
                  <Line
                    name='Adjusted Baseline'
                    dataKey={'baseline'}
                    stroke={CHARTCOLORS[0]}
                  />
                  <Line
                    name='Performance'
                    dataKey={'performance'}
                    stroke={CHARTCOLORS[1]}
                  />
                  {performanceModel.length > 0 && (
                    <Line
                      name='Performance Eval'
                      dataKey={'performance_eval'}
                      stroke={CHARTCOLORS[3]}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </Row>
          </Row>
          <Row style={{ width: '100%', paddingTop: '20px', fontSize: '18px' }}>
            <Col></Col>
            <Col>
              <EnergyTracerTable
                keyField='name'
                data={totalKeys}
                columns={[
                  { dataField: 'name', text: 'Period Total' },
                  {
                    dataField: 'value',
                    text: `Value (${UTILITY_TYPE_UNITS[regressionType][utilityType]})`,
                  },
                ]}
              />
              <EnergyTracerTable
                keyField='name'
                data={[
                  { name: 'Simple', value: simpleSavings },
                  { name: 'Performance', value: performanceSavings },
                ]}
                columns={[
                  { dataField: 'name', text: 'Savings' },
                  {
                    dataField: 'value',
                    text: `Value (${UTILITY_TYPE_UNITS[regressionType][utilityType]})`,
                  },
                ]}
              />
            </Col>
            <Col></Col>
          </Row>
        </Row>
      )}
    </>
  );
}
